/**
 * View State Hook — holds which widget surface is currently visible.
 *
 * Each setter names the target surface, so call sites read like direct
 * transitions (`openPreview()`, `openCard()`, ...) instead of back/forward
 * semantics that get ambiguous when the default entry point changes. Target
 * names mirror the Figma state taxonomy (pill / preview / card).
 *
 * Setters are stable across renders (`useCallback`), so passing them as props
 * to memoized children won't cause spurious re-renders.
 *
 *   openPreview    — collapsed-with-context state
 *   openPill       — minimal CTA state after dismissing the preview
 *   openCard       — expanded detail view
 *   reset          — return to the configured initial surface (`initial`)
 *
 * Note: there is deliberately no "dismiss the widget entirely" action today.
 * When a ticket introduces one, add both the setter and the matching branch
 * in `WidgetApp` in the same commit — the two must land together or the
 * widget will stay mounted after the dismiss call. If transitions ever grow
 * rules that depend on the previous state (e.g. "can't open card from pill"),
 * this is the place to upgrade to `useReducer`.
 */

import { useCallback, useState } from "preact/hooks";
import { WidgetView } from "../types";

export interface UseViewStateResult {
  view: WidgetView;
  openPill: () => void;
  openPreview: () => void;
  openCard: () => void;
  reset: () => void;
}

export function useViewState(
  initial: WidgetView = WidgetView.Preview,
): UseViewStateResult {
  const [view, setView] = useState<WidgetView>(initial);
  return {
    view,
    openPill: useCallback(() => setView(WidgetView.Pill), []),
    openPreview: useCallback(() => setView(WidgetView.Preview), []),
    openCard: useCallback(() => setView(WidgetView.Card), []),
    reset: useCallback(() => setView(initial), [initial]),
  };
}
